import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Analytics Stream Context
 * 
 * Provides a SINGLE WebSocket connection for real-time analytics events
 * that can be shared across all components. This prevents duplicate
 * connections and duplicate events.
 * 
 * Preferences are synced with the backend for persistence across devices.
 */

const MAX_EVENTS = 100;
const RECONNECT_DELAY = 3000;
const MAX_RECONNECT_ATTEMPTS = 5;
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AnalyticsStreamContext = createContext(null);

export function AnalyticsStreamProvider({ children }) {
  const { user, token } = useAuth();
  
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({
    retrievalsToday: 0,
    feedbackToday: 0,
    contentGapsToday: 0,
  });
  // User preferences for the event stream (loaded from localStorage first, then synced with backend)
  const [preferences, setPreferences] = useState(() => {
    const saved = localStorage.getItem('eva_stream_preferences');
    return saved ? JSON.parse(saved) : {
      enabled: true,
      soundEnabled: true,
      showNotifications: true,
    };
  });
  
  const wsRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const seenEventsRef = useRef(new Set()); // Track seen events to prevent duplicates
  
  // Fetch preferences from backend on mount
  useEffect(() => {
    if (!token || !user?.id) return;
    
    const fetchPreferences = async () => {
      try {
        const response = await fetch(`${API_URL}/api/users/me/preferences/event-stream`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          const newPrefs = {
            enabled: data.event_stream_enabled,
            soundEnabled: data.event_stream_sound,
            showNotifications: data.event_stream_notifications,
          };
          setPreferences(newPrefs);
          localStorage.setItem('eva_stream_preferences', JSON.stringify(newPrefs));
        }
      } catch (error) {
        console.warn('[Analytics Stream] Failed to fetch preferences:', error);
      }
    };
    
    fetchPreferences();
  }, [token, user?.id]);
  
  // Generate unique event ID
  const getEventId = useCallback((event) => {
    return `${event.type}-${event.timestamp}-${JSON.stringify(event.data || {})}`;
  }, []);
  
  const connect = useCallback(() => {
    if (!preferences.enabled || !user?.tenant_id || !token) return;
    
    // Prevent multiple connections
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log('[Analytics Stream] Already connected, skipping');
      return;
    }
    
    if (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING) {
      console.log('[Analytics Stream] Already connecting, skipping');
      return;
    }
    
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    // EIP-533: the access token is sent as the first frame, not in the URL.
    // A live JWT in a query string lands in proxy and load-balancer access
    // logs, browser history and Referer headers. tenant_id stays in the URL:
    // it is not a credential.
    const wsUrl = `${wsProtocol}://${wsHost}/api/ws/analytics?tenant_id=${user.tenant_id}`;

    setConnectionStatus('connecting');
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        // Authenticate before anything else. Every reconnect re-runs this
        // handler, so the backoff path re-sends the frame without needing
        // its own copy of this logic. The socket is not usable until the
        // server answers with {type:'connected'}, so the connected state is
        // set there rather than here -- which is also more honest than the
        // previous behaviour, where the UI said connected before the server
        // had validated anything.
        // Status stays 'connecting' until the server acknowledges: the
        // handshake is part of connecting, and inventing a new status value
        // would render as "Offline" in LiveBroadcast and
        // RealTimeActivityFeed, which both branch on the known set.
        try {
          ws.send(JSON.stringify({ token }));
        } catch (e) {
          console.warn('[Analytics Stream] Failed to send auth frame:', e);
        }
      };
      
      ws.onmessage = (event) => {
        try {
          // Handle raw ping/pong frames from proxies
          if (event.data === 'pong' || event.data === 'ping') {
            return;
          }
          
          const data = JSON.parse(event.data);
          
          // The server's 'connected' frame is the acknowledgement that the
          // auth handshake succeeded (EIP-533).
          if (data.type === 'connected') {
            setIsConnected(true);
            setConnectionStatus('connected');
            reconnectAttemptsRef.current = 0;
            console.log('[Analytics Stream Context] Connected');
            return;
          }

          // Handle heartbeat silently
          if (data.type === 'heartbeat' || data.type === 'pong') {
            return;
          }
          
          // De-duplicate events using unique ID
          const eventId = getEventId(data);
          if (seenEventsRef.current.has(eventId)) {
            console.log('[Analytics Stream] Duplicate event skipped:', eventId);
            return;
          }
          
          // Track this event
          seenEventsRef.current.add(eventId);
          
          // Cleanup old event IDs (keep last 500)
          if (seenEventsRef.current.size > 500) {
            const entries = Array.from(seenEventsRef.current);
            seenEventsRef.current = new Set(entries.slice(-250));
          }
          
          // Add event to state
          setEvents(prev => [...prev, data].slice(-MAX_EVENTS));
          
          // Update stats based on event type
          if (data.type === 'retrieval' || data.type === 'query') {
            setStats(prev => ({
              ...prev,
              retrievalsToday: prev.retrievalsToday + 1
            }));
          } else if (data.type === 'feedback' || data.type === 'thumbs_up' || data.type === 'thumbs_down') {
            setStats(prev => ({
              ...prev,
              feedbackToday: prev.feedbackToday + 1
            }));
          } else if (data.type === 'content_gap' && data.data?.is_new) {
            setStats(prev => ({
              ...prev,
              contentGapsToday: prev.contentGapsToday + 1
            }));
          }
          
        } catch (e) {
          console.warn('[Analytics Stream] Failed to parse message:', e);
        }
      };
      
      ws.onerror = () => {
        setConnectionStatus('error');
      };
      
      ws.onclose = (event) => {
        setIsConnected(false);
        wsRef.current = null;
        
        if (event.code === 4001) {
          setConnectionStatus('unauthorized');
          return;
        }
        
        if (event.code === 4003 || event.code === 403) {
          setConnectionStatus('limit_reached');
          return;
        }
        
        setConnectionStatus('disconnected');

        // Attempt reconnection with exponential backoff
        if (preferences.enabled && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS && event.code !== 1000) {
          reconnectAttemptsRef.current++;
          setConnectionStatus('reconnecting');

          const delay = RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current - 1);
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        }
      };
      
    } catch (error) {
      console.error('[Analytics Stream] Failed to create WebSocket:', error);
      setConnectionStatus('error');
    }
  }, [preferences.enabled, user?.tenant_id, token, getEventId]);
  
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    
    setIsConnected(false);
    setConnectionStatus('disconnected');
  }, []);
  
  const clearEvents = useCallback(() => {
    setEvents([]);
    seenEventsRef.current.clear();
  }, []);
  
  const resetStats = useCallback(() => {
    setStats({
      retrievalsToday: 0,
      feedbackToday: 0,
      contentGapsToday: 0,
    });
  }, []);
  
  // Update preferences (saves to both localStorage and backend)
  const updatePreferences = useCallback(async (newPrefs) => {
    const updated = { ...preferences, ...newPrefs };
    
    // Update local state immediately
    setPreferences(updated);
    localStorage.setItem('eva_stream_preferences', JSON.stringify(updated));
    
    // Sync with backend (non-blocking)
    if (token) {
      try {
        await fetch(`${API_URL}/api/users/me/preferences/event-stream`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            event_stream_enabled: updated.enabled,
            event_stream_sound: updated.soundEnabled,
            event_stream_notifications: updated.showNotifications,
          }),
        });
      } catch (error) {
        console.warn('[Analytics Stream] Failed to save preferences:', error);
      }
    }
  }, [preferences, token]);
  
  // Toggle stream enabled/disabled
  const toggleEnabled = useCallback(() => {
    updatePreferences({ enabled: !preferences.enabled });
  }, [preferences.enabled, updatePreferences]);
  
  // Toggle sound
  const toggleSound = useCallback(() => {
    updatePreferences({ soundEnabled: !preferences.soundEnabled });
  }, [preferences.soundEnabled, updatePreferences]);
  
  // Connect/disconnect based on preferences
  useEffect(() => {
    if (preferences.enabled && user?.tenant_id && token) {
      connect();
    } else {
      disconnect();
    }
    
    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- connect/disconnect are stable refs, intentionally excluded
  }, [preferences.enabled, user?.tenant_id, token]);
  
  // Send ping to keep connection alive
  useEffect(() => {
    if (!isConnected) return;

    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 25000);

    return () => clearInterval(pingInterval);
  }, [isConnected]);

  // EIP-326: deliberately close the WebSocket while the tab is hidden so a
  // backgrounded/hidden dashboard tab doesn't hold Cloud Run's request-based
  // billing open indefinitely (an open WS counts as an in-flight request, so
  // the instance never scales to zero). A 60s grace period avoids tearing
  // the connection down for a quick tab-switch. disconnect() already closes
  // with code 1000, and the onclose handler above already treats 1000 as
  // "don't reconnect" (see the `event.code !== 1000` check), so no extra
  // bookkeeping is needed to suppress the existing reconnect loop.
  useEffect(() => {
    const HIDDEN_GRACE_MS = 60000;
    let hiddenTimeoutId = null;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (hiddenTimeoutId) return;
        hiddenTimeoutId = setTimeout(() => {
          hiddenTimeoutId = null;
          if (!document.hidden) return;
          disconnect();
        }, HIDDEN_GRACE_MS);
        return;
      }

      if (hiddenTimeoutId) {
        clearTimeout(hiddenTimeoutId);
        hiddenTimeoutId = null;
      }

      const canConnect = preferences.enabled && !!user?.tenant_id && !!token;
      const isClosed = !wsRef.current || wsRef.current.readyState === WebSocket.CLOSED;
      if (canConnect && isClosed) {
        connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (hiddenTimeoutId) {
        clearTimeout(hiddenTimeoutId);
      }
    };
  }, [preferences.enabled, user?.tenant_id, token, connect, disconnect]);

  const value = useMemo(() => ({
    isConnected,
    connectionStatus,
    events,
    stats,
    preferences,
    connect,
    disconnect,
    clearEvents,
    resetStats,
    updatePreferences,
    toggleEnabled,
    toggleSound,
  }), [
    isConnected,
    connectionStatus,
    events,
    stats,
    preferences,
    connect,
    disconnect,
    clearEvents,
    resetStats,
    updatePreferences,
    toggleEnabled,
    toggleSound,
  ]);
  
  return (
    <AnalyticsStreamContext.Provider value={value}>
      {children}
    </AnalyticsStreamContext.Provider>
  );
}

/**
 * Hook to use the analytics stream
 */
export function useAnalyticsStream() {
  const context = useContext(AnalyticsStreamContext);
  if (!context) {
    throw new Error('useAnalyticsStream must be used within AnalyticsStreamProvider');
  }
  return context;
}

/**
 * Get events from the last N minutes
 */
export function useRecentEvents(events, minutes = 5) {
  const cutoff = Date.now() - (minutes * 60 * 1000);
  return useMemo(() => 
    events.filter(e => new Date(e.timestamp).getTime() > cutoff),
    [events, cutoff]
  );
}

export default AnalyticsStreamContext;
